import { useEffect, useState } from "react";
import type { Subscriber, SubscriberStatus } from "@akhshy/types";
import { api } from "../lib/api.ts";
import { errorMessage } from "../lib/errors.ts";
import { useToast } from "../lib/toast.tsx";
import { LoadingState, ErrorState, EmptyState } from "../components/molecules/StateBlock.tsx";

const STATUS_LABEL: Record<SubscriberStatus, string> = {
  active: "Active",
  unsubscribed: "Unsubscribed",
};

/** Date + time of a subscriber event, e.g. "Jul 6, 2026, 3:42 PM". */
function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  const d = new Date(iso);
  return Number.isNaN(d.getTime())
    ? "—"
    : d.toLocaleString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
        hour: "numeric",
        minute: "2-digit",
      });
}

export function SubscribersPage() {
  const toast = useToast();
  const [subs, setSubs] = useState<Subscriber[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [busyEmail, setBusyEmail] = useState<string | null>(null);

  const load = () => {
    setError(null);
    setSubs(null);
    api.subscribers
      .list()
      .then((res) => setSubs(res.items))
      .catch((err) => setError(errorMessage(err)));
  };

  useEffect(load, []);

  const counts = {
    active: subs?.filter((s) => s.status === "active").length ?? 0,
    unsubscribed: subs?.filter((s) => s.status === "unsubscribed").length ?? 0,
  };

  async function onSend() {
    if (counts.active === 0) {
      toast.error("No active subscribers to send to yet.");
      return;
    }
    if (!window.confirm(`Send the latest ritual to ${counts.active} active subscriber(s)?`)) {
      return;
    }
    setSending(true);
    try {
      const res = await api.subscribers.send();
      if (res.configured) toast.success(res.message);
      else toast.error(res.message);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setSending(false);
    }
  }

  async function onUnsubscribe(email: string) {
    if (!window.confirm(`Unsubscribe ${email}?`)) return;
    setBusyEmail(email);
    try {
      await api.subscribers.unsubscribe(email);
      toast.success("Unsubscribed");
      setSubs((prev) =>
        prev?.map((s) =>
          s.email === email ? { ...s, status: "unsubscribed", unsubscribedBy: "admin" } : s,
        ) ?? prev,
      );
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyEmail(null);
    }
  }

  async function onResubscribe(email: string) {
    if (!window.confirm(`Add ${email} back to the list?`)) return;
    setBusyEmail(email);
    try {
      await api.subscribers.resubscribe(email);
      toast.success("Resubscribed");
      setSubs((prev) =>
        prev?.map((s) =>
          s.email === email ? { ...s, status: "active", unsubscribedBy: null } : s,
        ) ?? prev,
      );
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyEmail(null);
    }
  }

  if (error) return <ErrorState message={error} onRetry={load} />;
  if (!subs) return <LoadingState />;

  return (
    <>
      <div className="overview-grid">
        <div className="stat-card">
          <div className="stat-num">{counts.active}</div>
          <div className="stat-label">Active</div>
          <div className="stat-sub">{counts.unsubscribed} unsubscribed</div>
        </div>
        <div className="stat-card">
          <div className="stat-num">{subs.length}</div>
          <div className="stat-label">Total subscribers</div>
          <div className="stat-sub">all time</div>
        </div>
        <div className="stat-card stat-card--action">
          <button
            type="button"
            className="btn btn-primary"
            onClick={onSend}
            disabled={sending || counts.active === 0}
          >
            {sending ? "Sending…" : "Send latest ritual"}
          </button>
          <p className="stat-sub stat-note">
            Emails the latest published ritual to active subscribers via Resend.
          </p>
        </div>
      </div>

      <section className="metrics-panel">
        <h2 className="metrics-heading">All subscribers</h2>
        {subs.length === 0 ? (
          <EmptyState>No one has subscribed yet.</EmptyState>
        ) : (
          <div className="panel table-scroll">
            <table className="data">
              <thead>
                <tr>
                  <th>Email</th>
                  <th>Status</th>
                  <th>Signed up</th>
                  <th>Active since</th>
                  <th aria-label="Actions" />
                </tr>
              </thead>
              <tbody>
                {subs.map((s) => (
                  <tr key={s.id}>
                    <td className="cell-title">{s.email}</td>
                    <td>
                      <span className={`sub-badge sub-badge--${s.status}`}>
                        {STATUS_LABEL[s.status]}
                      </span>
                    </td>
                    <td>{fmtDateTime(s.createdAt)}</td>
                    <td>{fmtDateTime(s.activatedAt)}</td>
                    <td className="cell-actions">
                      {s.status === "active" ? (
                        <button
                          type="button"
                          className="btn btn-danger btn-sm"
                          onClick={() => onUnsubscribe(s.email)}
                          disabled={busyEmail === s.email}
                        >
                          {busyEmail === s.email ? "…" : "Unsubscribe"}
                        </button>
                      ) : (
                        <button
                          type="button"
                          className="btn btn-sm"
                          onClick={() => onResubscribe(s.email)}
                          disabled={busyEmail === s.email || s.unsubscribedBy === "self"}
                          title={
                            s.unsubscribedBy === "self"
                              ? "They unsubscribed themselves — only they can re-subscribe."
                              : "Add them back to the list"
                          }
                        >
                          {busyEmail === s.email ? "…" : "Resubscribe"}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </>
  );
}
